// The Questions panel on an analytics dashboard: the "Questions?" tab and the
// chat it opens. GeneralLayout mounts it inside the page area, under the
// navigation, as a sibling of the dashboard's scroll container. It appears
// when the workspace has it switched on and the page reports a dashboard. The
// panel opens side-by-side (the dashboard narrows) or as a slide-over (the
// page dims behind it). Clients get the slide-over; staff can switch to compare.

import "../../index.css";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useSelector } from "react-redux";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { Button } from "@dashai/components/ui/button";
import { TooltipProvider } from "@dashai/components/ui/tooltip";
import { useAuth } from "@dashai/contexts/AuthContext";
import { DashAIAuthAdapter } from "@dashai/contexts/DashAIAuthAdapter";
import { useDashboardPanelChat, type DashboardPanelKey } from "@dashai/hooks/useDashboardPanelChat";
import { MessageFeedbackContext, useMessageFeedback } from "@dashai/hooks/useMessageFeedback";
import { usePanelStatus } from "@dashai/hooks/usePanelStatus";
import { cn } from "@dashai/lib/utils";
import type { Message } from "@dashai/types/dashai";
import { useDashboardQuestion, useQuestionsPanelOpen } from "@/contexts/DashboardQuestionContext";
import { useQuestionsPanelEnabled } from "@/hooks/useWorkspaceUiConfig";
import { isDashmoteStaffUser, selectActiveWorkspaceKey } from "@/slices/authSlice";

import { DASHBOARD_PANEL_INFO } from "./dashboards";
import { PANEL_LAYER } from "./layers";
import { PanelComposer } from "./PanelComposer";
import { PanelMessages } from "./PanelMessages";
import { QuestionsTab } from "./QuestionsTab";

type PanelLayout = "side-by-side" | "slide-over";

const LAYOUT_STORAGE_KEY = "dashone.questionsPanel.layout";

function readStoredLayout(): PanelLayout {
  try {
    return localStorage.getItem(LAYOUT_STORAGE_KEY) === "side-by-side"
      ? "side-by-side"
      : "slide-over";
  } catch {
    return "slide-over";
  }
}

function storeLayout(layout: PanelLayout): void {
  try {
    localStorage.setItem(LAYOUT_STORAGE_KEY, layout);
  } catch {
    // Storage refuses in private mode and past quota; the layout still holds for this visit.
  }
}

interface PanelState {
  auth: { userData?: unknown; isImpersonating?: boolean };
  filters: { currentCountry: string | null };
}

const PANEL_WIDTH = "w-[min(24rem,100%)]";

/**
 * Renders the panel when the workspace has it on and the page reports a
 * dashboard. The dashboard shell sits outside the DashAI routes and their auth
 * provider, so the slot brings its own, which supplies the workspace's brand
 * setup each question carries. The provider sits above the dashboard check, so
 * moving between dashboards keeps it and its fetched data.
 */
export function QuestionsPanelSlot() {
  const enabled = useQuestionsPanelEnabled();
  if (!enabled) return null;
  return (
    <DashAIAuthAdapter>
      <ReportedDashboardPanel />
    </DashAIAuthAdapter>
  );
}

function ReportedDashboardPanel() {
  const { dashboard } = useDashboardQuestion();
  return dashboard ? <QuestionsPanelContent dashboard={dashboard} /> : null;
}

function QuestionsPanelContent({ dashboard }: { dashboard: DashboardPanelKey }) {
  const [open, setOpen] = useQuestionsPanelOpen();
  const [staffLayout, setStaffLayout] = useState<PanelLayout>(readStoredLayout);
  const [scopeEl, setScopeEl] = useState<HTMLDivElement | null>(null);

  // Staff impersonating a client see what the client sees.
  const isStaff = useSelector(
    (s: PanelState) => !s.auth.isImpersonating && isDashmoteStaffUser(s.auth.userData)
  );
  const idClient: string = useSelector(selectActiveWorkspaceKey) ?? "";
  const country = useSelector((s: PanelState) => s.filters.currentCountry) ?? "";
  const { filters, view } = useDashboardQuestion();
  const { user } = useAuth();
  const userId = user?.uid ?? "";

  const status = usePanelStatus({ enabled: open, dashboard, view, userId, idClient, country });
  const chat = useDashboardPanelChat({ dashboard, view, userId, idClient, country, filters });

  // Thumbs votes run through the DashAI page's feedback hook, which reads the
  // conversation in its own message shape: to find a vote's chat, and to send
  // the transcript with a comment.
  const feedbackMessagesRef = useRef<Message[]>([]);
  useEffect(() => {
    feedbackMessagesRef.current = chat.messages.map((m) => ({
      id: m.storedId ?? m.id,
      user_id: user?.uid ?? "",
      chat_id: chat.chatId ?? "",
      role: m.role,
      message: m.content,
      created_at: m.createdAt,
    }));
  }, [chat.messages, chat.chatId, user?.uid]);
  const feedback = useMessageFeedback(user?.uid ?? "", idClient, feedbackMessagesRef);

  const info = DASHBOARD_PANEL_INFO[dashboard];
  const layout: PanelLayout = isStaff ? staffLayout : "slide-over";
  // dashaiChat decides whether this view can take a question. A refused
  // question carries the same reason when the status check could not run.
  const checking = status.status === "checking";
  const blockedReason =
    status.status === "blocked"
      ? status.reason
      : chat.failure === "blocked"
        ? chat.blockedReason
        : null;
  // A view the panel cannot answer for spans markets or has none, so naming one would mislead.
  const contextLine = [blockedReason ? "" : country, ...Object.values(filters)]
    .filter(Boolean)
    .join(" · ");

  const close = () => {
    chat.cancel();
    setOpen(false);
  };
  const toggle = () => (open ? close() : setOpen(true));
  const chooseLayout = (next: PanelLayout) => {
    setStaffLayout(next);
    storeLayout(next);
  };

  const renderBody = (title: ReactNode) => (
    <>
      <QuestionsTab open onToggle={close} className="right-full" />
      <div className="flex items-start gap-2 border-b border-border px-4 py-3">
        <div className="min-w-0 flex-1">
          {title}
          {contextLine && <p className="truncate text-xs text-muted-foreground">{contextLine}</p>}
        </div>
        {isStaff && <LayoutSwitch layout={layout} onChange={chooseLayout} />}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={close}
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      <PanelMessages
        dashboardName={info.name}
        messages={chat.messages}
        chatId={chat.chatId}
        suggestions={info.suggestions}
        isLoading={chat.isLoading}
        failure={chat.failure}
        country={country}
        checking={checking}
        blockedReason={blockedReason}
        onAsk={(q) => void chat.ask(q)}
        onRetry={chat.retry}
      />
      <PanelComposer
        dashboardName={info.name}
        disabled={chat.isLoading || checking || blockedReason !== null}
        placeholder={checking ? "Checking…" : undefined}
        restore={chat.failedQuestion}
        onSend={(q) => void chat.ask(q)}
      />
    </>
  );

  const titleText = `Ask about ${info.name}`;
  const panelClass = cn(
    "relative flex h-full flex-col border-l border-border bg-background text-foreground",
    PANEL_WIDTH
  );

  return (
    <TooltipProvider>
      <MessageFeedbackContext.Provider value={feedback}>
        <div ref={setScopeEl} className="dashai-scope contents" data-testid="questions-panel">
          {!open && <QuestionsTab open={false} onToggle={toggle} className="right-0" />}

          {open && layout === "side-by-side" && (
            <aside
              aria-label={titleText}
              className={cn(panelClass, "shrink-0", PANEL_LAYER)}
              onKeyDown={(e) => e.key === "Escape" && close()}
            >
              {renderBody(<h2 className="truncate text-sm font-semibold">{titleText}</h2>)}
            </aside>
          )}

          {layout === "slide-over" && (
            <Dialog.Root open={open} onOpenChange={(next) => (next ? setOpen(true) : close())}>
              <Dialog.Portal container={scopeEl}>
                <Dialog.Overlay
                  className="absolute inset-0 z-40 bg-black/20"
                  data-testid="questions-panel-backdrop"
                />
                <Dialog.Content
                  aria-describedby={undefined}
                  className={cn(panelClass, "absolute inset-y-0 right-0 shadow-xl", PANEL_LAYER)}
                >
                  {renderBody(
                    <Dialog.Title className="truncate text-sm font-semibold">
                      {titleText}
                    </Dialog.Title>
                  )}
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>
          )}
        </div>
      </MessageFeedbackContext.Provider>
    </TooltipProvider>
  );
}

function LayoutSwitch({
  layout,
  onChange,
}: {
  layout: PanelLayout;
  onChange: (l: PanelLayout) => void;
}) {
  const options: Array<[PanelLayout, string, string]> = [
    ["side-by-side", "A", "Side-by-side layout"],
    ["slide-over", "B", "Slide-over layout"],
  ];
  return (
    <div
      role="group"
      aria-label="Panel layout"
      className="flex rounded-md border border-border p-0.5"
    >
      {options.map(([value, label, description]) => (
        <button
          key={value}
          type="button"
          aria-pressed={layout === value}
          aria-label={description}
          onClick={() => onChange(value)}
          className={cn(
            "rounded px-2 py-0.5 text-xs font-medium",
            layout === value
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
